import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import moment from 'moment';

import ERRORS from '../../constants/error';
import APPCONSTANTS from '../../constants/appConstants';
import DetailCard from '../../components/detailCard/DetailCard';
import CustomTable from '../../components/customtable/CustomTable';
import { enrolledPatientSelector, loadingSelector, totalSelector } from '../../store/report/selectors';
import { fetchEnrolledPatientRequest } from '../../store/report/actions';
import toastCenter from '../../utils/toastCenter';
import Loader from '../../components/loader/Loader';
import FilterLayout from '../../components/filter/FilterLayout';
import { downloadEnrolledPatientsReport } from '../../services/reportAPI';
import { firstNameSelector, lastNameSelector, roleSelector } from '../../store/user/selectors';
import { getPageLocationWithParams, trackGoogleAnalyticsEvent } from '../../utils/commonUtils';

interface IMatchParams {
  siteId: string;
  siteName: string;
}

const EnrolledPatientsReport = (props: IMatchParams): React.ReactElement => {
  const fromDatePickerRef = useRef<DatePicker>(null);
  const toDatePickerRef = useRef<DatePicker>(null);
  const [listParams, setListReqParams] = useState({
    page: APPCONSTANTS.NUMBER.ONE,
    rowsPerPage: APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE,
    searchTerm: ''
  });

  const [fromDate, setFromDate] = useState<Date | null | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | null | undefined>(undefined);
  const [gender, setGender] = useState<Array<null | string>>([]);
  const [HTNDiagnosis, setHTNDiagnosis] = useState(false);
  const [DBMDiagnosis, setDBMDiagnosis] = useState(false);
  const [ProvisionalDiagnosis, setProvisionalDiagnosis] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const dispatch = useDispatch();

  const enrolledPatientList = useSelector(enrolledPatientSelector);
  const loading = useSelector(loadingSelector);
  const enrolledPatientCount = useSelector(totalSelector);
  const role: any = useSelector(roleSelector);
  const firstName: string = useSelector(firstNameSelector) || '';
  const lastName: string = useSelector(lastNameSelector) || '';

  const { siteId, siteName } = useParams<IMatchParams>();

  const prevDateFetchValue = useRef<any>({
    fromDate: null,
    toDate: null
  });

  useEffect(() => {
    if (!fromDate) {
      toDatePickerRef.current?.props.onChange(null, undefined);
      setToDate(null);
    }
    if ((fromDate && !toDate) || (fromDate && toDate)) {
      toDatePickerRef.current?.props.minDate?.setDate(fromDate.getDate());
      toDatePickerRef.current?.props.minDate?.setMonth(fromDate.getMonth());
      toDatePickerRef.current?.props.minDate?.setFullYear(fromDate.getFullYear());
    }
    if (toDate) {
      trackGoogleAnalyticsEvent(
        APPCONSTANTS.USER_ACTIONS.FILTERS.DATE_FILTER,
        APPCONSTANTS.REPORT_LIST.ENROLLED_PATIENTS,
        getPageLocationWithParams(),
        { username: `${firstName} ${lastName}`, role: role.label }
      );
      fromDatePickerRef.current?.props.maxDate?.setDate(toDate.getDate());
      fromDatePickerRef.current?.props.maxDate?.setMonth(toDate.getMonth());
      fromDatePickerRef.current?.props.maxDate?.setFullYear(toDate.getFullYear());
    }
  }, [toDatePickerRef, fromDate, toDate, loading, exportLoading, firstName, lastName, role.label]);

  const reqPayload = (newParams: any) => {
    const reqData = {
      healthFacilityId: Number(siteId),
      skip: (newParams.page - APPCONSTANTS.NUMBER.ONE) * newParams.rowsPerPage,
      limit: newParams.rowsPerPage,
      failureCb: (e: Error) => {
        if (e.message === ERRORS.NETWORK_ERROR.message) {
          toastCenter.error(APPCONSTANTS.NETWORK_ERROR, APPCONSTANTS.CONNECTION_LOST);
        } else {
          toastCenter.error(APPCONSTANTS.OOPS, APPCONSTANTS.ENROLLED_PATIENTS_FETCH_ERROR);
        }
      }
    };
    if (fromDate && toDate) {
      Object.assign(reqData, {
        fromDate: moment(fromDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT),
        toDate: moment(toDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT)
      });
    }
    if (gender.length) {
      Object.assign(reqData, { genders: gender });
    }
    if (HTNDiagnosis) {
      Object.assign(reqData, { htnDiagnosis: HTNDiagnosis });
    }
    if (DBMDiagnosis) {
      Object.assign(reqData, { dbmDiagnosis: DBMDiagnosis });
    }
    if (ProvisionalDiagnosis) {
      Object.assign(reqData, { provisionalDiagnosis: ProvisionalDiagnosis });
    }
    return reqData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  const fetchList = (newParams: any) => {
    dispatch(fetchEnrolledPatientRequest({ payload: reqPayload(newParams) }));
    setListReqParams(newParams);
  };

  useEffect(() => {
    trackGoogleAnalyticsEvent(
      APPCONSTANTS.USER_ACTIONS.REPORT_VIEW,
      APPCONSTANTS.REPORT_LIST.ENROLLED_PATIENTS,
      getPageLocationWithParams(),
      { username: `${firstName} ${lastName}`, role: role.label }
    );
    const newParams = {
      ...listParams,
      page: 1
    };
    fetchList(newParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, gender, HTNDiagnosis, DBMDiagnosis, ProvisionalDiagnosis, dispatch]);

  useEffect(() => {
    const formatDate = (date: any) => (date ? moment(date).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT) : null);
    const prevFromDate = formatDate(prevDateFetchValue.current?.fromDate);
    const prevToDate = formatDate(prevDateFetchValue.current?.toDate);
    const currFromDate = formatDate(fromDate);
    const currToDate = formatDate(toDate);
    const valueChanged = () => {
      return prevFromDate !== currFromDate || prevToDate !== currToDate;
    };
    if ((fromDate && toDate && valueChanged()) || (fromDate === null && toDate === null && valueChanged())) {
      prevDateFetchValue.current = {
        fromDate: currFromDate,
        toDate: currToDate
      };
      const newParams = {
        ...listParams,
        page: 1
      };
      fetchList(newParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, dispatch]);

  const handleSearch = (searchString: string) => {
    const newParams = {
      ...listParams,
      page: APPCONSTANTS.NUMBER.ONE,
      searchTerm: searchString
    };
    fetchList(newParams);
  };

  const handlePage = (pageNo: number, rowsPerPage: number | undefined = APPCONSTANTS.ROWS_PER_PAGE_OF_TABLE) => {
    const newParams = {
      ...listParams,
      page: pageNo,
      rowsPerPage
    };
    fetchList(newParams);
  };

  const handleGenderOnChange = (event: React.BaseSyntheticEvent) => {
    if (event.target.checked) {
      trackGoogleAnalyticsEvent(
        APPCONSTANTS.USER_ACTIONS.FILTERS.GENDER_FILTER,
        APPCONSTANTS.REPORT_LIST.ENROLLED_PATIENTS,
        getPageLocationWithParams(),
        { username: `${firstName} ${lastName}`, role: role.label }
      );
      setGender((values) => [...values, event.target.value]);
    } else {
      const index = gender?.indexOf(event.target.value);
      const updatedValue = gender;
      updatedValue.splice(index, 1);
      setGender((values) => [...updatedValue]);
    }
  };

  const exportEnrolledPatients = async () => {
    try {
      setExportLoading(true);
      const payLoad = {
        healthFacilityId: Number(siteId),
        siteName,
        limit: enrolledPatientCount
      };
      if (fromDate && toDate) {
        Object.assign(payLoad, {
          fromDate: moment(fromDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT),
          toDate: moment(toDate).format(APPCONSTANTS.YEAR_MONTH_DATE_FORMAT)
        });
      }
      if (gender.length) {
        Object.assign(payLoad, { genders: gender });
      }
      if (HTNDiagnosis) {
        Object.assign(payLoad, { htnDiagnosis: HTNDiagnosis });
      }
      if (DBMDiagnosis) {
        Object.assign(payLoad, { dbmDiagnosis: DBMDiagnosis });
      }
      if (ProvisionalDiagnosis) {
        Object.assign(payLoad, { provisionalDiagnosis: ProvisionalDiagnosis });
      }
      await downloadEnrolledPatientsReport(payLoad);
      trackGoogleAnalyticsEvent(
        APPCONSTANTS.USER_ACTIONS.REPORT_DOWNLOAD,
        APPCONSTANTS.REPORT_LIST.ENROLLED_PATIENTS,
        getPageLocationWithParams(),
        { username: `${firstName} ${lastName}`, role: role.label }
      );
      setExportLoading(false);
      toastCenter.success(APPCONSTANTS.SUCCESS, APPCONSTANTS.REPORT_DOWNLOAD_SUCCESS);
    } catch {
      setExportLoading(false);
      toastCenter.error(APPCONSTANTS.ERROR, APPCONSTANTS.DOWNLOAD_FAIL_MSG);
    }
  };
  return (
    <>
      {loading && <Loader />}
      {exportLoading && <Loader />}
      <div className='row'>
        <FilterLayout
          fromDateSelector={(date: Date) => {
            setFromDate(date);
          }}
          toDateSelector={(date: Date) => setToDate(date)}
          handleGenderOnChange={(event: React.BaseSyntheticEvent) => handleGenderOnChange(event)}
          HTNOnChange={(event: React.BaseSyntheticEvent) => {
            trackGoogleAnalyticsEvent(
              APPCONSTANTS.USER_ACTIONS.FILTERS.DIAGNOSIS_FILTER,
              APPCONSTANTS.REPORT_LIST.ENROLLED_PATIENTS,
              getPageLocationWithParams(),
              { username: `${firstName} ${lastName}`, role: role.label }
            );
            setHTNDiagnosis(event.target.checked);
          }}
          DBMOnChange={(event: React.BaseSyntheticEvent) => {
            trackGoogleAnalyticsEvent(
              APPCONSTANTS.USER_ACTIONS.FILTERS.DIAGNOSIS_FILTER,
              APPCONSTANTS.REPORT_LIST.ENROLLED_PATIENTS,
              getPageLocationWithParams(),
              { username: `${firstName} ${lastName}`, role: role.label }
            );
            setDBMDiagnosis(event.target.checked);
          }}
          ProvisionalOnChange={(event: React.BaseSyntheticEvent) => {
            trackGoogleAnalyticsEvent(
              APPCONSTANTS.USER_ACTIONS.FILTERS.DIAGNOSIS_FILTER,
              APPCONSTANTS.REPORT_LIST.ENROLLED_PATIENTS,
              getPageLocationWithParams(),
              { username: `${firstName} ${lastName}`, role: role.label }
            );
            setProvisionalDiagnosis(event.target.checked);
          }}
          fromDate={fromDate}
          fromDatePickerRef={fromDatePickerRef}
          toDatePickerRef={toDatePickerRef}
          showProvisionalDiagnosis={true}
        />
        <div className='col'>
          <DetailCard
            header='Enrolled Patients'
            isSearch={false}
            onSearch={handleSearch}
            buttonLabel='Export'
            onButtonClick={() => exportEnrolledPatients()}
            isDownload={true}
            isDisabled={enrolledPatientList.length === 0 ? true : false}
          >
            <CustomTable
              rowData={enrolledPatientList}
              columnsDef={[
                {
                  id: 1,
                  name: 'firstName',
                  label: 'Name'
                },
                {
                  id: 2,
                  name: 'gender',
                  label: 'Gender'
                },
                {
                  id: 3,
                  name: 'phoneNumber',
                  label: 'Phone Number',
                  class: 'numeric'
                },
                {
                  id: 4,
                  name: 'provisional_diagnosis',
                  label: 'Provisional Diagnosis',
                  cellFormatter: (data: any) => {
                    if (data?.provisionalDiagnosis == null) {
                      return '';
                    } else {
                      return String(data.provisionalDiagnosis);
                    }
                  }
                },
                {
                  id: 5,
                  name: 'confirm_diagnosis',
                  label: 'Confirm Diagnosis',
                  width: '260px',
                  cellFormatter: (data: any) => {
                    if (data?.confirmDiagnosis == null) {
                      return '';
                    } else {
                      return String(data.confirmDiagnosis);
                    }
                  }
                },
                {
                  id: 7,
                  name: 'enrollmentAt',
                  label: 'Created On',
                  class: 'numeric',
                  cellFormatter: (data: any) => {
                    if (data?.enrollmentAt) {
                      return moment(data.enrollmentAt).format(APPCONSTANTS.DATE_MONTH_YEAR_FORMAT);
                    } else {
                      return '';
                    }
                  }
                }
              ]}
              isDelete={false}
              isEdit={false}
              isView={false}
              isDownload={false}
              page={listParams.page}
              rowsPerPage={listParams.rowsPerPage}
              count={enrolledPatientCount}
              handlePageChange={handlePage}
            />
          </DetailCard>
        </div>
      </div>
    </>
  );
};

export default EnrolledPatientsReport;
